import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import { DEFAULT_WHATSAPP_DIGITS, normalizeWhatsAppDigits } from '../utils/whatsapp';
import './Footer.css';

const DEFAULT_PHONE = '218917597979';
const SETTINGS_CACHE_KEY = 'footer_settings_cache';

const formatPhoneDisplay = (digits) => `+${digits || DEFAULT_PHONE}`;

const getCachedSettings = () => {
  try {
    const raw = sessionStorage.getItem(SETTINGS_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch (_) {}
  return null;
};

const setCachedSettings = (data) => {
  try {
    if (data && typeof data === 'object') {
      sessionStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(data));
    }
  } catch (_) {}
};

const Footer = () => {
  const [settings, setSettings] = useState(() => getCachedSettings());

  useEffect(() => {
    let cancelled = false;
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.getSettings();
        if (cancelled) return;
        const data = response.data;
        setSettings(data);
        setCachedSettings(data);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching settings:', err);
        setSettings((prev) => prev || { site_name: 'حمزة لقطع غيار مرسيدس بنز', site_description: '' });
      }
    };
    fetchSettings();
    return () => { cancelled = true; };
  }, []);

  const isPlaceholder = (val) => !val || /x+/i.test(String(val));
  const phoneDigits = settings && settings.phone && !isPlaceholder(settings.phone)
    ? String(settings.phone).replace(/\D/g, '')
    : DEFAULT_PHONE;
  const whatsappDigits =
    settings && settings.whatsapp_number && !isPlaceholder(settings.whatsapp_number)
      ? normalizeWhatsAppDigits(settings.whatsapp_number)
      : DEFAULT_WHATSAPP_DIGITS;
  const phoneDisplay = formatPhoneDisplay(phoneDigits);
  const whatsappDisplay = formatPhoneDisplay(whatsappDigits);

  const isLoading = !settings;

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">
              <img src="/logo.png" alt="حمزة لقطع غيار مرسيدس" className="footer-logo-img" />
            </div>
            <h3 className="footer-title">{settings?.site_name || 'حمزة لقطع غيار مرسيدس بنز'}</h3>
            <p className="footer-description">{isLoading ? 'جاري التحميل...' : (settings?.site_description || '')}</p>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-heading">معلومات الاتصال</h4>
            <p className="footer-info">
              <span className="footer-icon">📞</span>
              <a href={`tel:${phoneDigits}`}>{phoneDisplay}</a>
            </p>
            <p className="footer-info">
              <span className="footer-icon">📱</span>
              <a href={`https://wa.me/${whatsappDigits}`} target="_blank" rel="noopener noreferrer">
                {whatsappDisplay}
              </a>
            </p>
            {settings?.email && (
              <p className="footer-info">
                <span className="footer-icon">✉️</span>
                <a href={`mailto:${settings.email}`}>{settings.email}</a>
              </p>
            )}
            {settings?.address && (
              <p className="footer-info">
                <span className="footer-icon">📍</span>
                {settings.address}
              </p>
            )}
            {settings?.working_hours && (
              <p className="footer-info">
                <span className="footer-icon">🕐</span>
                {settings.working_hours}
              </p>
            )}
          </div>
          
          <div className="footer-section">
            <h4 className="footer-heading">تابعنا</h4>
            <div className="social-links">
              <a href={settings?.facebook_url || 'https://www.facebook.com/profile.php?id=61551859033094'} target="_blank" rel="noopener noreferrer" className="social-link social-link-fb" aria-label="فيسبوك">
                <i className="fab fa-facebook-f" aria-hidden="true" />
              </a>
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="social-link">
                  Instagram
                </a>
              )}
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} {settings?.site_name || 'حمزة لقطع غيار مرسيدس بنز'}. جميع الحقوق محفوظة.</p>
          <p>hamzabenz.ly</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
